import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../hooks';
import { Modal } from '../../components/shared/Modal';
import styles from './Admin.module.css';

export const Route = createFileRoute('/admin/organizations')({
  component: AdminOrganizationsRoute,
});

function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

interface OrgListItem {
  id: string;
  name: string;
  orgSlug: string;
  planTierId: string | null;
  createdAt: string;
  deactivatedAt: string | null;
  projectCount: number;
  poolCount: number;
}

interface OrgResources {
  projects: number;
  applications: number;
  applicationVersions: number;
  deploymentProfiles: number;
  activeDeploymentProfiles: number;
  jwksKeys: number;
  customHostnames: number;
  poolsWithInstances: Array<{ poolId: string; poolName: string; instanceCount: number }>;
}

type FilterMode = 'all' | 'active' | 'deactivated';

function AdminOrganizationsRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<OrgListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Deactivate modal
  const [deactivateOrg, setDeactivateOrg] = useState<OrgListItem | null>(null);
  const [deactivateResources, setDeactivateResources] = useState<OrgResources | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [deactivateBlockers, setDeactivateBlockers] = useState<Array<{ type: string; detail: string }>>([]);

  // Reactivate modal
  const [reactivateOrg, setReactivateOrg] = useState<OrgListItem | null>(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  // Delete modal
  const [deleteOrg, setDeleteOrg] = useState<OrgListItem | null>(null);
  const [deleteResources, setDeleteResources] = useState<OrgResources | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteStep, setDeleteStep] = useState<'review' | 'confirm'>('review');

  useEffect(() => {
    if (isPlatformAdmin === false) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const fetchOrgs = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { organizations: OrgListItem[] };
      const fetched = data.organizations ?? [];
      fetched.sort((a, b) => a.name.localeCompare(b.name));
      setOrgs(fetched);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isPlatformAdmin) fetchOrgs();
  }, [isPlatformAdmin, fetchOrgs]);

  const fetchResources = useCallback(async (orgId: string): Promise<OrgResources | null> => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(orgId)}/resources`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { resources: OrgResources };
      return data.resources;
    } catch {
      return null;
    }
  }, [authHeaders]);

  // Filter + search
  const filteredOrgs = useMemo(() => {
    let list = orgs;
    if (filterMode === 'active') list = list.filter(o => !o.deactivatedAt);
    else if (filterMode === 'deactivated') list = list.filter(o => !!o.deactivatedAt);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o => o.name.toLowerCase().includes(q) || o.orgSlug.toLowerCase().includes(q));
    }
    return list;
  }, [orgs, filterMode, search]);

  // Deactivate flow
  const openDeactivate = useCallback(async (org: OrgListItem) => {
    setDeactivateOrg(org);
    setDeactivateError(null);
    setDeactivateBlockers([]);
    setDeactivateLoading(true);
    const resources = await fetchResources(org.id);
    setDeactivateResources(resources);
    setDeactivateLoading(false);
  }, [fetchResources]);

  const confirmDeactivate = useCallback(async () => {
    if (!deactivateOrg) return;
    setDeactivateLoading(true);
    setDeactivateError(null);
    setDeactivateBlockers([]);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(deactivateOrg.id)}/deactivate`, {
        method: 'PATCH',
        headers,
      });
      const data = await res.json() as any;
      if (!res.ok) {
        if (data.blockers) {
          setDeactivateBlockers(data.blockers);
        }
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setDeactivateOrg(null);
      await fetchOrgs();
    } catch (err: any) {
      setDeactivateError(err.message);
    } finally {
      setDeactivateLoading(false);
    }
  }, [deactivateOrg, authHeaders, fetchOrgs]);

  // Reactivate flow
  const confirmReactivate = useCallback(async () => {
    if (!reactivateOrg) return;
    setReactivateLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(reactivateOrg.id)}/reactivate`, {
        method: 'PATCH',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setReactivateOrg(null);
      await fetchOrgs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setReactivateLoading(false);
    }
  }, [reactivateOrg, authHeaders, fetchOrgs]);

  // Delete flow
  const openDelete = useCallback(async (org: OrgListItem) => {
    setDeleteOrg(org);
    setDeleteError(null);
    setDeleteConfirmName('');
    setDeleteStep('review');
    setDeleteLoading(true);
    const resources = await fetchResources(org.id);
    setDeleteResources(resources);
    setDeleteLoading(false);
  }, [fetchResources]);

  const confirmDelete = useCallback(async () => {
    if (!deleteOrg) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(deleteOrg.id)}`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: deleteConfirmName }),
      });
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDeleteOrg(null);
      await fetchOrgs();
    } catch (err: any) {
      setDeleteError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteOrg, deleteConfirmName, authHeaders, fetchOrgs]);

  if (!isPlatformAdmin) return null;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Organizations</h1>
        <div className={styles.sectionTabs}>
          {(['all', 'active', 'deactivated'] as const).map(mode => (
            <button
              key={mode}
              className={`${styles.sectionTab} ${filterMode === mode ? styles.active : ''}`}
              onClick={() => setFilterMode(mode)}
            >
              {mode}
              <span className={styles.count}>
                {mode === 'all' ? orgs.length : mode === 'active' ? orgs.filter(o => !o.deactivatedAt).length : orgs.filter(o => !!o.deactivatedAt).length}
              </span>
            </button>
          ))}
        </div>
      </header>

      <div className={styles.searchBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search organizations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && <div className={styles.loading}>Loading organizations...</div>}

      {!loading && filteredOrgs.length === 0 && (
        <div className={styles.empty}>No organizations found</div>
      )}

      {!loading && filteredOrgs.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Projects</th>
              <th>Pools</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map(org => (
              <tr key={org.id} style={org.deactivatedAt ? { opacity: 0.5 } : undefined}>
                <td>
                  <div className={styles.poolName}>{org.name}</div>
                  <div className={styles.poolMeta}>{org.id}</div>
                </td>
                <td className={styles.poolCell}>{org.orgSlug}</td>
                <td>
                  {org.deactivatedAt ? (
                    <span className={styles.badgeInactive}>Deactivated</span>
                  ) : (
                    <span className={styles.badgeActive}>Active</span>
                  )}
                </td>
                <td className={styles.poolCell}>{org.projectCount}</td>
                <td className={styles.poolCell}>{org.poolCount}</td>
                <td className={styles.poolCell}>{new Date(org.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className={styles.poolActions}>
                    {!org.deactivatedAt ? (
                      <button className={styles.actionButton} onClick={() => openDeactivate(org)}>
                        Deactivate
                      </button>
                    ) : (
                      <>
                        <button className={styles.actionButton} onClick={() => setReactivateOrg(org)}>
                          Reactivate
                        </button>
                        <button className={styles.deleteButton} onClick={() => openDelete(org)}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Deactivate Modal */}
      <Modal
        isOpen={!!deactivateOrg}
        title={`Deactivate "${deactivateOrg?.name}"`}
        onClose={() => setDeactivateOrg(null)}
        size="medium"
      >
        <div className={styles.formContent}>
          {deactivateLoading && !deactivateResources && (
            <div className={styles.loading}>Loading resource summary...</div>
          )}

          {deactivateResources && (
            <div className={styles.formSection}>
              <h3 className={styles.formSectionTitle}>Resource Summary</h3>
              <ResourceSummary resources={deactivateResources} />
            </div>
          )}

          {deactivateBlockers.length > 0 && (
            <div className={styles.error}>
              <strong>Cannot deactivate — clean up these resources first:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {deactivateBlockers.map((b, i) => (
                  <li key={i}>{b.detail}</li>
                ))}
              </ul>
            </div>
          )}

          {deactivateError && !deactivateBlockers.length && (
            <div className={styles.error}>{deactivateError}</div>
          )}

          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
            Deactivating will hide this organization from all non-admin interfaces.
            It can be reactivated later.
          </p>

          <div className={styles.confirmActions}>
            <button className={styles.actionButton} onClick={() => setDeactivateOrg(null)}>
              Cancel
            </button>
            <button
              className={styles.deleteButton}
              onClick={confirmDeactivate}
              disabled={deactivateLoading || deactivateBlockers.length > 0}
            >
              {deactivateLoading ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reactivate Modal */}
      <Modal
        isOpen={!!reactivateOrg}
        title={`Reactivate "${reactivateOrg?.name}"`}
        onClose={() => setReactivateOrg(null)}
        size="small"
      >
        <div className={styles.formContent}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
            This will restore the organization and make it visible across the platform again.
          </p>
          <div className={styles.confirmActions}>
            <button className={styles.actionButton} onClick={() => setReactivateOrg(null)}>
              Cancel
            </button>
            <button
              className={styles.applyButton}
              onClick={confirmReactivate}
              disabled={reactivateLoading}
            >
              {reactivateLoading ? 'Reactivating...' : 'Reactivate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteOrg}
        title={`Delete "${deleteOrg?.name}"`}
        onClose={() => setDeleteOrg(null)}
        size="medium"
        disableOverlayClick
      >
        <div className={styles.formContent}>
          {deleteLoading && !deleteResources && (
            <div className={styles.loading}>Loading resource summary...</div>
          )}

          {deleteStep === 'review' && deleteResources && (
            <>
              <div className={styles.error}>
                <strong>This action is irreversible.</strong> All data associated with this
                organization will be permanently deleted.
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Records to be deleted</h3>
                <ResourceSummary resources={deleteResources} />
              </div>

              <div className={styles.confirmActions}>
                <button className={styles.actionButton} onClick={() => setDeleteOrg(null)}>
                  Cancel
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => setDeleteStep('confirm')}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {deleteStep === 'confirm' && (
            <>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                Type <strong>{deleteOrg?.name}</strong> to confirm deletion:
              </p>
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Type organization name..."
                value={deleteConfirmName}
                onChange={e => setDeleteConfirmName(e.target.value)}
                autoFocus
              />

              {deleteError && <div className={styles.error}>{deleteError}</div>}

              <div className={styles.confirmActions}>
                <button className={styles.actionButton} onClick={() => setDeleteOrg(null)}>
                  Cancel
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={confirmDelete}
                  disabled={deleteLoading || deleteConfirmName !== deleteOrg?.name}
                >
                  {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

function ResourceSummary({ resources }: { resources: OrgResources }) {
  const items = [
    { label: 'Projects', count: resources.projects },
    { label: 'Applications', count: resources.applications },
    { label: 'Application Versions', count: resources.applicationVersions },
    { label: 'Deployment Profiles', count: resources.deploymentProfiles },
    { label: 'Active Profiles', count: resources.activeDeploymentProfiles },
    { label: 'JWKS Keys', count: resources.jwksKeys },
    { label: 'Custom Hostnames', count: resources.customHostnames },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
      {items.map(item => (
        <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
          <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.count}</span>
        </div>
      ))}
      {resources.poolsWithInstances.length > 0 && (
        <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c0392b', marginBottom: '4px' }}>
            Pools with running instances
          </div>
          {resources.poolsWithInstances.map(p => (
            <div key={p.poolId} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', padding: '2px 0' }}>
              {p.poolName} — {p.instanceCount} instance(s)
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
